<template>
  <div class="profile-view">
    <p class="profile-subtitle">Manage your details, the workspace you’re currently active in, and the orgs and tenants you can switch into.</p>

    <SectionTabs v-model="activeTab" :tabs="tabs" aria-label="Account tabs" />

    <!-- Personal tab -->
    <section v-if="activeTab === 'personal'" class="profile-card">
      <h2 class="card-title">Personal Details</h2>
      <p class="card-blurb">These come from your sign-up. Inline editing is coming soon — for now reach out to the team to change name, email or phone.</p>
      <div class="details-grid">
        <div class="detail-row">
          <label>Name</label>
          <p>{{ fullName || '—' }}</p>
        </div>
        <div class="detail-row">
          <label>Email</label>
          <p>{{ authStore.userDetails.email || '—' }}</p>
        </div>
        <div class="detail-row">
          <label>Phone</label>
          <p>{{ authStore.userDetails.phoneNumber || 'Not set' }}</p>
        </div>
        <div class="detail-row">
          <label>User ID</label>
          <p class="mono small">{{ authStore.userId || '—' }}</p>
        </div>
      </div>
    </section>

    <!-- Memberships tab -->
    <section v-else-if="activeTab === 'memberships'" class="profile-card">
      <h2 class="card-title">Workspaces</h2>
      <p class="card-blurb">All Orgs and Tenants you have access to. The active one is highlighted — it’s the workspace your actions count against right now.</p>

      <div v-if="authStore.isLoadingMemberships" class="state-message">Loading memberships…</div>
      <div v-else-if="authStore.memberships.length === 0" class="state-message">
        No memberships loaded. Backend may not be wired yet —
        <button type="button" class="link-btn" @click="reloadMemberships">retry</button>.
      </div>
      <ul v-else class="memberships-list">
        <li
          v-for="membership in authStore.memberships"
          :key="membership.kind + ':' + membership.entityId"
          :class="{ active: membership.isActive, 'has-members-open': isMembersOpen(membership) }"
        >
          <div class="membership-row-main">
            <div class="membership-row-identity">
              <span class="membership-kind">{{ membership.kind === 'org' ? 'Org' : 'Tenant' }}</span>
              <span class="membership-name">{{ membership.name || membership.entityId }}</span>
              <span v-if="membership.isActive" class="active-badge">Active</span>
            </div>
            <div class="membership-meta">
              <button
                v-if="canManageMembers(membership)"
                type="button"
                class="secondary-btn small"
                @click="toggleMembers(membership)"
              >
                {{ isMembersOpen(membership) ? 'Hide Members' : 'Members' }}
              </button>
              <button
                v-if="canRenameMembership(membership)"
                type="button"
                class="secondary-btn small"
                :disabled="renaming"
                @click="renameMembership(membership)"
              >
                Rename
              </button>
              <button
                v-if="!membership.isActive"
                type="button"
                class="primary-btn small"
                :disabled="authStore.isSwitchingContext"
                @click="switchTo(membership)"
              >
                Switch
              </button>
            </div>
          </div>

          <div v-if="isMembersOpen(membership)" class="members-panel">
            <div v-if="membersState[memberKey(membership)]?.loading" class="state-message">Loading members…</div>
            <div v-else-if="membersState[memberKey(membership)]?.error" class="state-message error">
              {{ membersState[memberKey(membership)]?.error }}
            </div>
            <ul v-else-if="(membersState[memberKey(membership)]?.rows || []).length" class="members-list">
              <li v-for="m in membersState[memberKey(membership)]?.rows" :key="m.sub" class="member-row">
                <div>
                  <span class="member-name">{{ m.name || m.email || m.sub }}</span>
                  <small class="member-email">{{ m.email }}</small>
                </div>
                <div class="member-meta">
                  <span class="role-badge small">{{ m.role }}</span>
                  <button
                    v-if="m.sub !== authStore.userId"
                    type="button"
                    class="secondary-btn small danger"
                    :disabled="membersState[memberKey(membership)]?.busy"
                    @click="removeMember(membership, m)"
                  >
                    Remove
                  </button>
                  <span v-else class="active-badge">You</span>
                </div>
              </li>
            </ul>
            <p v-else class="state-message">No members yet.</p>

            <form class="invite-form" @submit.prevent="inviteMember(membership)">
              <input
                type="email"
                placeholder="invitee@example.com"
                v-model="membersState[memberKey(membership)].draftEmail"
                required
              />
              <select v-model="membersState[memberKey(membership)].draftRole">
                <option v-for="r in roleOptionsFor(membership)" :key="r" :value="r">{{ r }}</option>
              </select>
              <button
                type="submit"
                class="primary-btn small"
                :disabled="membersState[memberKey(membership)]?.busy"
              >
                Invite
              </button>
            </form>
            <small class="invite-hint">If they already have an account they’re added instantly. Otherwise we’ll give you a magic link to share — they accept it on signup.</small>

            <div
              v-if="membersState[memberKey(membership)]?.pendingMagicLink"
              class="magic-link-card"
            >
              <p>
                <strong>{{ membersState[memberKey(membership)]?.pendingEmail }}</strong>
                isn’t signed up yet. Send them this link — they’ll accept the invite as soon as they finish signup:
              </p>
              <div class="magic-link-row">
                <input
                  type="text"
                  readonly
                  :value="membersState[memberKey(membership)]?.pendingMagicLink"
                  @focus="(e) => (e.target as HTMLInputElement).select()"
                />
                <button type="button" class="primary-btn small" @click="copyMagicLink(membership)">Copy</button>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div v-if="canCreateTenant" class="memberships-footer">
        <button
          type="button"
          class="secondary-btn"
          :disabled="creatingTenant"
          @click="createNewTenant"
        >
          + New Tenant
        </button>
      </div>
    </section>

    <!-- Modals -->
    <PromptModal
      :open="promptDialog.open"
      :title="promptDialog.title"
      :description="promptDialog.description"
      :placeholder="promptDialog.placeholder"
      :initial-value="promptDialog.initialValue"
      :confirm-label="promptDialog.confirmLabel"
      @confirm="onPromptConfirm"
      @cancel="onPromptCancel"
    />
    <ConfirmModal
      :open="confirmDialog.open"
      :title="confirmDialog.title"
      :description="confirmDialog.description"
      :confirm-label="confirmDialog.confirmLabel"
      :cancel-label="confirmDialog.cancelLabel"
      :tone="confirmDialog.tone"
      @confirm="onConfirmConfirm"
      @cancel="onConfirmCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useAuthStore } from '@/store/auth.store'
import { profileService, type Membership, type MemberRow } from '@/services/profile'
import SectionTabs, { type SectionTab } from '@/components/shell/SectionTabs.vue'
import PromptModal from '@/components/shell/PromptModal.vue'
import ConfirmModal from '@/components/shell/ConfirmModal.vue'

interface MembersPanelState {
  open: boolean
  loading: boolean
  busy: boolean
  error: string
  rows: MemberRow[]
  draftEmail: string
  draftRole: string
  pendingMagicLink?: string
  pendingEmail?: string
}

const ORG_INVITE_ROLES = ['ORG_ADMIN', 'ORG_MEMBER', 'ORG_VIEWER']
const TENANT_INVITE_ROLES = ['TENANT_ADMIN', 'TENANT_MEMBER', 'TENANT_VIEWER']
const ORG_MANAGE_MEMBER_ROLES = new Set(['ORG_OWNER', 'ORG_ADMIN'])
const TENANT_MANAGE_MEMBER_ROLES = new Set(['TENANT_ADMIN'])
const ORG_RENAME_ROLES = new Set(['ORG_OWNER', 'ORG_ADMIN'])
const TENANT_RENAME_ROLES = new Set(['TENANT_ADMIN'])
const TENANT_CREATE_ROLES = new Set(['ORG_OWNER', 'ORG_ADMIN'])

const membersState = reactive<Record<string, MembersPanelState>>({})
const memberKey = (m: Membership): string => `${m.kind}:${m.entityId}`

const authStore = useAuthStore()
const renaming = ref(false)
const creatingTenant = ref(false)
const activeTab = ref<string>('personal')

const tabs = computed<SectionTab[]>(() => [
  { id: 'personal', label: 'Personal' },
  { id: 'memberships', label: 'Workspaces', badge: authStore.memberships.length || undefined },
])

// --- Promise-returning prompt/confirm dialogs (replace native window.prompt / window.confirm) ---

const promptDialog = reactive({
  open: false,
  title: '',
  description: '',
  placeholder: '',
  initialValue: '',
  confirmLabel: 'Save',
  resolve: null as ((v: string | null) => void) | null,
})

const confirmDialog = reactive<{
  open: boolean
  title: string
  description: string
  confirmLabel: string
  cancelLabel: string
  tone: 'info' | 'danger'
  resolve: ((v: boolean) => void) | null
}>({
  open: false,
  title: '',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  tone: 'info',
  resolve: null,
})

function showPrompt(opts: {
  title: string
  description?: string
  placeholder?: string
  initialValue?: string
  confirmLabel?: string
}): Promise<string | null> {
  return new Promise(resolve => {
    promptDialog.title = opts.title
    promptDialog.description = opts.description ?? ''
    promptDialog.placeholder = opts.placeholder ?? ''
    promptDialog.initialValue = opts.initialValue ?? ''
    promptDialog.confirmLabel = opts.confirmLabel ?? 'Save'
    promptDialog.open = true
    promptDialog.resolve = resolve
  })
}

function showConfirm(opts: {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'info' | 'danger'
}): Promise<boolean> {
  return new Promise(resolve => {
    confirmDialog.title = opts.title
    confirmDialog.description = opts.description ?? ''
    confirmDialog.confirmLabel = opts.confirmLabel ?? 'Confirm'
    confirmDialog.cancelLabel = opts.cancelLabel ?? 'Cancel'
    confirmDialog.tone = opts.tone ?? 'info'
    confirmDialog.open = true
    confirmDialog.resolve = resolve
  })
}

function onPromptConfirm(value: string) {
  promptDialog.open = false
  promptDialog.resolve?.(value)
  promptDialog.resolve = null
}
function onPromptCancel() {
  promptDialog.open = false
  promptDialog.resolve?.(null)
  promptDialog.resolve = null
}
function onConfirmConfirm() {
  confirmDialog.open = false
  confirmDialog.resolve?.(true)
  confirmDialog.resolve = null
}
function onConfirmCancel() {
  confirmDialog.open = false
  confirmDialog.resolve?.(false)
  confirmDialog.resolve = null
}

// --- Computed identity helpers ---

const fullName = computed(() => {
  const parts = [authStore.userDetails.name, authStore.userDetails.surname].filter(Boolean)
  return parts.join(' ').trim()
})

const orgMemberships = computed(() =>
  authStore.memberships.filter((m) => m.kind === 'org'),
)

const activeOrg = computed(() =>
  orgMemberships.value.find((m) => m.isActive)
    || orgMemberships.value.find((m) => m.entityId === authStore.activeOrgId),
)

const activeOrgRoleForRenameCheck = computed(() => activeOrg.value?.role || authStore.activeOrgRole || '')

const canCreateTenant = computed(() =>
  TENANT_CREATE_ROLES.has(activeOrgRoleForRenameCheck.value),
)

const canRenameMembership = (m: Membership): boolean => {
  if (m.kind === 'org') return ORG_RENAME_ROLES.has(m.role)
  if (TENANT_RENAME_ROLES.has(m.role)) return true
  const parentOrg = orgMemberships.value.find((o) => o.entityId === m.orgId)
  return Boolean(parentOrg && ORG_RENAME_ROLES.has(parentOrg.role))
}

const canManageMembers = (m: Membership): boolean => {
  if (m.kind === 'org') return ORG_MANAGE_MEMBER_ROLES.has(m.role)
  if (TENANT_MANAGE_MEMBER_ROLES.has(m.role)) return true
  const parentOrg = orgMemberships.value.find((o) => o.entityId === m.orgId)
  return Boolean(parentOrg && ORG_MANAGE_MEMBER_ROLES.has(parentOrg.role))
}

// --- Members panel state ---

const ensureMembersState = (m: Membership): MembersPanelState => {
  const k = memberKey(m)
  if (!membersState[k]) {
    membersState[k] = {
      open: false,
      loading: false,
      busy: false,
      error: '',
      rows: [],
      draftEmail: '',
      draftRole: m.kind === 'org' ? ORG_INVITE_ROLES[1] : TENANT_INVITE_ROLES[1],
    }
  }
  return membersState[k]
}

const isMembersOpen = (m: Membership): boolean => Boolean(membersState[memberKey(m)]?.open)

const roleOptionsFor = (m: Membership): string[] =>
  m.kind === 'org' ? ORG_INVITE_ROLES : TENANT_INVITE_ROLES

const toggleMembers = async (m: Membership) => {
  const state = ensureMembersState(m)
  if (state.open) {
    state.open = false
    return
  }
  state.open = true
  state.loading = true
  state.error = ''
  try {
    state.rows = m.kind === 'org'
      ? await profileService.listOrgMembers(m.entityId)
      : await profileService.listTenantMembers(m.entityId)
  } catch (e: unknown) {
    state.error = (e as Error)?.message || 'Failed to load members'
  } finally {
    state.loading = false
  }
}

const inviteMember = async (m: Membership) => {
  const state = ensureMembersState(m)
  const email = state.draftEmail.trim()
  const role = state.draftRole
  if (!email || !role) return
  state.busy = true
  state.error = ''
  state.pendingMagicLink = undefined
  state.pendingEmail = undefined
  try {
    const result = m.kind === 'org'
      ? await profileService.inviteOrgMember(m.entityId, email, role)
      : await profileService.inviteTenantMember(m.entityId, email, role)
    if (result.kind === 'instant') {
      state.rows = [result.member, ...state.rows.filter((r) => r.sub !== result.member.sub)]
    } else {
      state.pendingMagicLink = result.invitation.magicLink
      state.pendingEmail = result.invitation.email
    }
    state.draftEmail = ''
  } catch (e: unknown) {
    const err = e as { response?: { data?: { error?: string } }, message?: string }
    state.error = err?.response?.data?.error || err?.message || 'Invite failed'
  } finally {
    state.busy = false
  }
}

const copyMagicLink = async (m: Membership) => {
  const state = ensureMembersState(m)
  if (!state.pendingMagicLink) return
  try {
    await navigator.clipboard.writeText(state.pendingMagicLink)
  } catch {
    /* clipboard unavailable */
  }
}

const removeMember = async (m: Membership, target: MemberRow) => {
  const ok = await showConfirm({
    title: `Remove ${target.name || target.email}?`,
    description: 'They’ll lose access to this workspace immediately. You can re-invite them later.',
    confirmLabel: 'Remove',
    tone: 'danger',
  })
  if (!ok) return
  const state = ensureMembersState(m)
  state.busy = true
  state.error = ''
  try {
    if (m.kind === 'org') {
      await profileService.removeOrgMember(m.entityId, target.sub)
    } else {
      await profileService.removeTenantMember(m.entityId, target.sub)
    }
    state.rows = state.rows.filter((r) => r.sub !== target.sub)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { error?: string } }, message?: string }
    state.error = err?.response?.data?.error || err?.message || 'Remove failed'
  } finally {
    state.busy = false
  }
}

const reloadMemberships = async () => {
  await authStore.loadMemberships()
}

// --- Rename / create / switch handlers using new modals ---

const renameMembership = async (m: Membership) => {
  const label = m.kind === 'org' ? 'organisation' : 'tenant'
  const next = await showPrompt({
    title: `Rename ${label}`,
    description: `Change the display name of this ${label}. Members see this name everywhere.`,
    placeholder: `New ${label} name`,
    initialValue: m.name || m.entityId,
    confirmLabel: 'Save',
  })
  if (next === null) return
  const trimmed = next.trim()
  if (!trimmed || trimmed === (m.name || '').trim()) return
  renaming.value = true
  try {
    if (m.kind === 'org') {
      await profileService.renameOrg(m.entityId, trimmed)
    } else {
      await profileService.renameTenant(m.entityId, trimmed)
    }
    await authStore.loadMemberships()
  } catch (e) {
    console.error('Rename failed', e)
    await showConfirm({
      title: 'Rename failed',
      description: `We couldn’t rename this ${label}. Please try again or check the console.`,
      confirmLabel: 'Got it',
      cancelLabel: 'Close',
      tone: 'danger',
    })
  } finally {
    renaming.value = false
  }
}

const createNewTenant = async () => {
  const requested = await showPrompt({
    title: 'Create a new tenant',
    description: 'Tenants are sub-brands inside your organisation. Leave the name blank to auto-generate one.',
    placeholder: 'Tenant name (optional)',
    confirmLabel: 'Create',
  })
  if (requested === null) return
  creatingTenant.value = true
  try {
    await profileService.createTenant(requested.trim() || undefined)
    await authStore.loadMemberships()
  } catch (e) {
    console.error('Create tenant failed', e)
    await showConfirm({
      title: 'Could not create tenant',
      description: 'Something went wrong. Please try again.',
      confirmLabel: 'Got it',
      cancelLabel: 'Close',
      tone: 'danger',
    })
  } finally {
    creatingTenant.value = false
  }
}

const switchTo = async (membership: Membership) => {
  if (membership.isActive) return
  const label = membership.kind === 'org' ? 'Org' : 'Tenant'
  const ok = await showConfirm({
    title: `Switch to ${label} "${membership.name || membership.entityId}"?`,
    description: 'Your unsaved browser drafts will be cleared and your session will refresh.',
    confirmLabel: 'Switch',
    tone: 'info',
  })
  if (!ok) return

  try {
    if (membership.kind === 'org') {
      await authStore.switchActiveContext({ orgId: membership.entityId })
    } else {
      await authStore.switchActiveContext({ tenantId: membership.entityId })
    }
    await authStore.loadMemberships()
  } catch (e) {
    console.error('Switch failed', e)
    await showConfirm({
      title: 'Switch failed',
      description: 'We couldn’t switch workspaces. Please try again.',
      confirmLabel: 'Got it',
      cancelLabel: 'Close',
      tone: 'danger',
    })
  }
}

onMounted(() => {
  authStore.loadMemberships()
})
</script>

<style scoped>
.profile-view {
  --profile-pill-bg: #2c3e50;
  --profile-button-bg: #96ffc9;
  --profile-button-bg-hover: #7ae6b1;
  --profile-button-text: #1f2933;
  max-width: 880px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  text-align: left;
  color: var(--color-text);
  font-family: var(--font-sans);
}

.profile-subtitle {
  margin: 0;
  color: var(--color-text);
  opacity: 0.7;
  font-size: 0.95rem;
  font-weight: 300;
}

.profile-card {
  background-color: var(--profile-pill-bg);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: clamp(1rem, 2vw, 1.5rem);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
}

.profile-card--placeholder {
  background-color: rgba(44, 62, 80, 0.5);
  border-style: dashed;
}

.card-title {
  margin: 0 0 0.6rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-heading);
}

.card-blurb {
  margin: 0 0 1rem;
  color: var(--color-text);
  opacity: 0.7;
  font-size: 0.9rem;
  font-weight: 300;
  line-height: 1.5;
}

.placeholder-list {
  margin: 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.placeholder-list li {
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
}
.placeholder-list li::marker {
  color: rgba(150, 255, 201, 0.5);
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.detail-row label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-heading);
  opacity: 0.65;
  margin-bottom: 0.3rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.detail-row p {
  margin: 0;
  color: var(--color-text);
  font-weight: 300;
}

.mono.small {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  word-break: break-all;
}

.role-badge {
  display: inline-block;
  margin-top: 0.4rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.memberships-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.memberships-list li {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.memberships-list li.active {
  border-color: rgba(150, 255, 201, 0.5);
  box-shadow: inset 3px 0 0 #96ffc9;
  padding-left: 1rem;
}

.membership-row-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.membership-row-identity {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.membership-kind {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.5);
}

.membership-name {
  font-weight: 700;
  color: #ffffff;
}

.membership-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.membership-meta > button {
  flex: 0 0 auto;
}

.active-badge {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: #96ffc9;
  color: #181b2f;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.state-message {
  color: var(--color-text);
  opacity: 0.75;
  font-size: 0.9rem;
  font-weight: 300;
}
.state-message.error {
  color: #fa9696;
}

.link-btn {
  background: var(--profile-button-bg);
  border: 1px solid var(--profile-button-bg);
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  color: var(--profile-button-text);
  cursor: pointer;
  font-weight: 700;
  text-decoration: none;
  font-family: var(--font-sans);
}

.primary-btn,
.secondary-btn,
.link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  border-radius: 999px;
  padding: 0.55rem 1.1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 0.85rem;
  border: 1px solid var(--profile-button-bg);
  background: var(--profile-button-bg);
  color: var(--profile-button-text);
  transition: background 0.15s ease;
  white-space: nowrap;
}

.secondary-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.15);
}

.primary-btn:hover:not(:disabled) {
  background: var(--profile-button-bg-hover);
}
.secondary-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.25);
}

.primary-btn:disabled,
.secondary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-btn.small,
.secondary-btn.small {
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
}

.secondary-btn.danger {
  border-color: rgba(250, 150, 150, 0.4);
  color: #fa9696;
}
.secondary-btn.danger:hover:not(:disabled) {
  background: rgba(250, 150, 150, 0.1);
}

.memberships-list li.has-members-open {
  background: rgba(255, 255, 255, 0.05);
}

.members-panel {
  background: rgba(0, 0, 0, 0.2);
  border: 1px dashed rgba(150, 255, 201, 0.25);
  border-radius: 10px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.members-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.member-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.member-name {
  font-weight: 600;
  color: #ffffff;
  display: block;
}

.member-email {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  display: block;
}

.member-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.role-badge.small {
  font-size: 0.65rem;
  padding: 0.1rem 0.5rem;
  margin-top: 0;
}

.invite-form {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.invite-form input[type='email'],
.invite-form select {
  flex: 1;
  min-width: 200px;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: var(--color-text);
  font-size: 0.85rem;
  font-family: var(--font-sans);
}

.invite-form select {
  flex: 0 0 auto;
  min-width: 130px;
}

.invite-hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  font-weight: 300;
}

.magic-link-card {
  background: rgba(150, 255, 201, 0.08);
  border: 1px solid rgba(150, 255, 201, 0.2);
  border-radius: 10px;
  padding: 0.75rem;
  font-size: 0.85rem;
}
.magic-link-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.magic-link-row input {
  flex: 1;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.25);
  color: #ffffff;
  font-size: 0.8rem;
  font-family: ui-monospace, monospace;
}

.memberships-footer {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 640px) {
  .membership-row-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }
  .membership-meta {
    width: auto;
    justify-content: flex-start;
  }
  .invite-form input[type='email'],
  .invite-form select {
    min-width: 0;
    width: 100%;
  }
  .invite-form {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
